import { type AccountFlows, accountKeys } from '@taler/api-client'
import { useQuery } from '@tanstack/vue-query'
import { type CurrencyCode } from '@taler/domain'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import type { PeriodRange } from '../internal/flow-period.ts'

export interface UseAccountFlowsOptions {
  readonly ledgerId: () => string | undefined
  readonly accountId: () => string
  readonly range: () => PeriodRange
  readonly displayCurrency: () => CurrencyCode
  readonly enabled: () => boolean
}

export interface UseAccountFlows {
  readonly flows: ComputedRef<AccountFlows | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

export function useAccountFlows(options: UseAccountFlowsOptions): UseAccountFlows {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = () => ({ ...options.range(), displayCurrency: options.displayCurrency() })

  const flowsQuery = useQuery({
    queryKey: computed(() => accountKeys.flows(options.ledgerId() ?? '', options.accountId(), query())),
    queryFn: ({ signal }) => api.accounts.flows(options.ledgerId()!, options.accountId(), query(), signal),
    enabled: active,
    retry: false,
  })

  return {
    flows: computed(() => flowsQuery.data.value),
    isLoading: computed(() => active.value && flowsQuery.isPending.value),
    error: computed(() => flowsQuery.error.value),
    retrying: retrying(flowsQuery),
    retry: () => {
      void flowsQuery.refetch()
    },
  }
}
